'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslations } from 'next-intl';
import { useRef, useEffect } from 'react';

import { UiPickupPointInfo } from '@/ui/pickup-point-info';
import { UiKitLoader } from '@/ui-kit/loader';
import { UiKitSelect } from '@/ui-kit/select';
import { iconPickupPointTypes } from '@/util/map-point-maps/mock';
import {
  PointFeature,
  PointType,
  PickupPointTypeOption
} from '@/util/map-point-maps/types';

import { styles } from './UiPickupPointSelector.styles';

export interface UiPickupPointSelectorProps {
  pointsList?: PointFeature[];
  pointTypesOptions: PickupPointTypeOption[];
  selectedPointType: PointType | null;
  onSelectPoint: (point: PointFeature) => void;
  activePointId: string | null;
  onPointTypeChange: (pointType: PointType | null) => void;
  onPointConfirm: (pointId: string) => void;
  pointSelectorTitle?: string;
  isHasPointTypeSelector?: boolean;
  size?: 'small' | 'default';
  hideConfirmButton?: boolean;
  loading?: boolean;
  isFreeShipping?: boolean;
}

export function UiPickupPointSelector({
  onSelectPoint,
  activePointId,
  pointsList = [],
  pointTypesOptions,
  selectedPointType,
  onPointTypeChange,
  onPointConfirm,
  pointSelectorTitle,
  isHasPointTypeSelector = true,
  size = 'default',
  hideConfirmButton = false,
  loading = false,
  isFreeShipping
}: UiPickupPointSelectorProps) {
  const slots = styles({ size });

  const t = useTranslations();

  const pointListRef = useRef<HTMLDivElement>(null);

  const options = pointTypesOptions;

  const selectedPoint =
    options.find((option) => option.value === selectedPointType) || null;

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: pointsList.length,
    getItemKey: (index) => pointsList[index].id,
    estimateSize: () => 80,
    getScrollElement: () => pointListRef.current
  });

  const virtualizerItems = virtualizer.getVirtualItems();

  useEffect(() => {
    if (pointsList.length === 0) return;

    const activePointIndex = pointsList.findIndex((point) => point.id === activePointId);

    if (activePointIndex < 0) return;

    virtualizer.scrollToIndex(activePointIndex, { align: 'start' });
  }, [activePointId, pointsList, virtualizer]);

  const isEmpty = pointsList.length === 0;
  const listHeight = isEmpty ? undefined : virtualizer.getTotalSize();
  const listTransform = isEmpty
    ? undefined
    : `translateY(${virtualizerItems[0]?.start ?? 0}px)`;

  return (
    <div className={slots.mobileContainer()}>
      <div className={slots.base()}>
        <h2 className={slots.title({ withMargin: !isHasPointTypeSelector })}>
          {pointSelectorTitle || t('pickupPoints')}
        </h2>
        {isHasPointTypeSelector && options.length > 0 && (
          <div className={slots.pointTypeSelector()}>
            <UiKitSelect
              classNameStateListBox="map"
              label={t('pickupServices.title')}
              selected={selectedPoint}
              onChange={(option) => {
                onPointTypeChange((option?.value as PointType) || null);
              }}
              icons={iconPickupPointTypes}
              placeholder={t('pickupServices.title')}
              options={options}
              inputClassName="placeholder:normal-case"
              noInput
              renderOption={(option: PickupPointTypeOption) => {
                const Icon = option.icon && iconPickupPointTypes[option.icon];

                return (
                  <span className={slots.option()}>
                    {option.icon && (
                      <Icon
                        width={36}
                        height={36}
                      />
                    )}
                    {option.label}
                  </span>
                );
              }}
            />
          </div>
        )}
        <div
          ref={pointListRef}
          className={slots.pointsScrollWrapper()}
        >
          <div
            className={slots.pointsScrollInner()}
            style={listHeight !== undefined ? { height: listHeight } : undefined}
          >
            <div
              className={slots.pointList()}
              style={listTransform ? { transform: listTransform } : undefined}
            >
              {isEmpty
                ? !loading && (
                    <div className={slots.emptyState()}>{t('noPointsFound')}</div>
                  )
                : virtualizerItems.map((virtualItem) => {
                    const point = pointsList[virtualItem.index];
                    const isActive = point.id === activePointId;

                    return (
                      <div
                        key={virtualItem.key}
                        data-index={virtualItem.index}
                        ref={virtualizer.measureElement}
                        className={slots.pointItemContainer()}
                      >
                        <div
                          role="button"
                          className={slots.pointItem({ isActive })}
                          onClick={() => onSelectPoint(point)}
                        >
                          <UiPickupPointInfo
                            point={point}
                            activePointId={activePointId}
                            onPointConfirm={onPointConfirm}
                            hideConfirmButton={hideConfirmButton}
                            isFreeShipping={isFreeShipping}
                          />
                        </div>
                      </div>
                    );
                  })}
            </div>
          </div>
        </div>
        <UiKitLoader show={loading} />
      </div>
    </div>
  );
}
